import {
  Component, OnDestroy, OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CatalogSuggestionsResponseComponent } from './components/catalog-suggestions-response/catalog-suggestions-response.component';
import { CatalogSuggestionsService } from 'src/app/services/catalog-suggestions.service';
import { CatalogSuggestionsDetailsComponent } from './components/catalog-suggestions-details/catalog-suggestions-details.component';
import { AuthService } from 'src/app/services/auth.service';
import { UpdateCourseComponent } from '../course-catalog/components/update-course/update-course.component';
import { EntityService } from 'src/app/services/entity.service';
import { formatDate } from 'src/app/utils';
import { filter, take } from 'rxjs/operators';

interface SectionType {
  id: number;
  text: string;
  icon: string;
  includedCatalogStatuses: string;
}

@Component({
  selector: 'app-catalog-suggestions',
  templateUrl: './catalog-suggestions.component.html',
  styleUrls: [ './catalog-suggestions.component.scss' ],
})

export class CatalogSuggestionsComponent implements OnInit, OnDestroy {
  
  defaultSections: SectionType[] = [
    {
      id: 0, text: 'Pending Suggestions', icon: 'pending_actions', includedCatalogStatuses: 'SUGGESTION_PENDING',
    },
    {
      id: 1, text: 'Denied Suggestions', icon: 'do_not_disturb', includedCatalogStatuses: 'SUGGESTION_DENIED',
    },
  ]
  selectedSection: SectionType = this.defaultSections[0]
  isLoading = false;
  tableData: any;
  courseDataSubscription: any;
  entityTimezoneSubscription: any;
  _coursesData: any;
  offset = 0;
  selectedItemsPerPage = 25;
  onlyShowManagedUsers = null;
  sortColumnName = 'id';
  sortDirection = 'DESC';
  originalCatalogSuggestionsResponse: any;
  filterString = '';

  manageAllUsers = false;

  permissionsService = this.authService.permissions$.subscribe((permissions) => {
    if (!permissions) return;
    const { MANAGE_ALL_AUTHORITY } = permissions;

    if (MANAGE_ALL_AUTHORITY == 1) {
      this.manageAllUsers = true;
    }
  });

  constructor(
    public dialog: MatDialog,
    private catalogSuggestionsService: CatalogSuggestionsService,
    private authService: AuthService,
    private entityService: EntityService,
  ) { }

  private getDateCellContent(session: any): { dateContent: string; timeContent: string } {
    if (!session) return { dateContent: 'No date', timeContent: '' };

    const isAsync = session.preRecordedOption === 'ASYNC';
    const isAnytime = session.preRecordedOption === 'ANYTIME';

    if (isAsync) return { dateContent: 'Async', timeContent: '' };

    if (isAnytime) {
      return {
        dateContent: formatDate.asDateLocal(session.dateOffered) || 'Anytime',
        timeContent: 'Anytime',
      };
    }

    const tz = this.entityService.getLocalTimezoneWithFallback();
    const startDate = formatDate.parseSessionTimeAsUtc(session.sessionStartDate);
    const endDate = formatDate.parseSessionTimeAsUtc(session.sessionEndDate);
    const startTime = startDate ? formatDate.asTimeWithTimezone(startDate, tz) : '';
    const endTime = endDate ? formatDate.asTimeWithTimezone(endDate, tz) : '';

    return {
      dateContent: formatDate.asDateLocal(session.dateOffered) || '-',
      timeContent: startTime && endTime ? `${startTime} - ${endTime}` : '',
    };
  }

  ngOnInit(): void {
    this.catalogSuggestionsService.clearcatalogSuggestionsData();
    this.isLoading = true;
    this.courseDataSubscription = this.catalogSuggestionsService.catalogSuggestionsData.subscribe((data: any) => {
  this._coursesData = data;
  this.originalCatalogSuggestionsResponse = { ...data }

  if (!data) {
    this.isLoading = false;
    return;
  }

  if (!Array.isArray(data.rows)) {
    console.warn('[CatalogSuggestionsComponent] data.rows is not an array:', data.rows);
    this.isLoading = false;
    return;
  }

  const formatedTableData = data?.rows?.map((course: any, index: number) => {
    const courseItem = course.CourseItems?.[0] ?? course.items?.[0] ?? null;

    let dateContent = 'No date';
    let timeContent = '';
    
    try {
      const cell = this.getDateCellContent(courseItem);
      dateContent = cell.dateContent;
      timeContent = cell.timeContent;
    } catch (error) {
      console.error('[CatalogSuggestionsComponent] Error formatting date/time for row', index, error);
      dateContent = 'Invalid date';
    }

    return {
      itemId: course.id,
      itemData: [
        {
          content: course.courseName || 'Unnamed Course',
          class: 'kbody-medium left-align bold',
          secondLine: course.id,
        },
        { 
          content: course.trackingValue || '0',
          class: 'skinny left-align' 
        },
        {
          content: dateContent,
          secondLine: timeContent,
          class: 'medium-medium left-align bold',
        },
        {
          avatar: { initials: [ course.SubmitUser?.firstName?.charAt(0) || '?', course.SubmitUser?.lastName?.charAt(0) || '?' ] },
          content: course.SubmitUser?.fullName || 'Unknown User',
          secondLine: course.SubmitUser?.contactEmail || '',
          class: 'wide left-align bold',
        },
        { 
          content: course.createdDate ? new Date(course.createdDate).toLocaleDateString() : 'N/A',
          class: 'medium-skinny left-align' 
        },
      ],
    };
  });

  this.tableData = {
    tableTitle: this.selectedSection.text,
    error: data?.error || null,
    columnTitles: [
      { title: 'Course', sortBy: 'courseName' },
      {
        title: 'Hours', class: 'skinny left-align', sortBy: 'courseName',
      },
      {
        title: 'Date & Time', class: 'medium-medium left-align', icon: 'date_range', sortBy: 'startDate',
      },
      {
        title: 'Submitted by', class: 'wide left-align', icon: 'person', sortBy: 'courseName',
      },
      {
        title: 'Submitted on', class: 'medium-skinny left-align', sortBy: 'id',
      },
    ],
    activeTotal: data?.counts?.activeCount,
    inactiveTotal: data?.counts?.inactiveCount,
    totalItems: data?.pagination?.totalItems,
    curPage: data?.pagination?.curPage,
    totalPages: data?.pagination?.totalPages,
    data: formatedTableData,
  };
  
  this.isLoading = false;
});

    if (this.entityService.getLocalTimezone()) {
      this.getSuggestions();
    } else {
      this.entityService.getEntity('Config');
      this.entityTimezoneSubscription = this.entityService.entityData$.pipe(
        filter((entity: any) => !!entity),
        take(1),
      ).subscribe(() => this.getSuggestions());
    }
  }

getSuggestions = () => {
  this.catalogSuggestionsService.clearcatalogSuggestionsData();
  this.isLoading = true;

  let queryParams: any = {
    activeFlag: 1,
    courseNameFilter: this.filterString,
    sortColumn: this.sortColumnName,
    sortDirection: this.sortDirection,
    includedAssociations: 'Items, SubmitUser',
    includedCatalogStatuses: this.selectedSection.includedCatalogStatuses,
    limit: this.selectedItemsPerPage,
    offset: this.offset,
  };

  if (this.onlyShowManagedUsers || !this.manageAllUsers) {
    queryParams = { ...queryParams, includeOnlyManagedUsers: true };
  }

  this.catalogSuggestionsService.getManagedCatalogSuggestions(queryParams);
}


  calculateOffset(page: number): number {
    return (page - 1) * this.selectedItemsPerPage;
  }

  onPageChanged(page: number) {
    const offset = this.calculateOffset(page);
    this.offset = offset;
    this.getSuggestions();
  }

  onItemsPerPageChange(itemsPerPage: number) {
    this.selectedItemsPerPage = itemsPerPage;
    this.offset = 0;
    this.getSuggestions();
  }

  toggleSelectedSection = (section: SectionType) => {
    this.tableData.data = [];
    this.selectedSection = section
    this.offset = 0;
    if (section.id === 3) { return }
    this.getSuggestions()
  }

  searchCatalogSuggestionss(filterString: any) {
    const { searchText } = filterString;
    this.filterString = searchText;
    this.offset = 0;
    this.getSuggestions();
  }

  sortColumn = (sortOrder: any) => {
    this.sortColumnName = sortOrder.sortBy;
    this.sortDirection = sortOrder.direction;

    const currentPage = this.originalCatalogSuggestionsResponse?.pagination?.curPage || 1;
    const offset = this.calculateOffset(currentPage);
    this.offset = offset;

    this.getSuggestions();
  }

  showItemDetails = (item: any) => {
    const selectedCourse = this._coursesData?.rows?.find((course: any) => course.id === item);
    const dialogRef = this.dialog.open(CatalogSuggestionsDetailsComponent, { data: selectedCourse })
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.openCatalogSuggestionsResponse({ ...selectedCourse, itemId: selectedCourse.id }, result);
      }
    });
  }

  openCatalogSuggestionsResponse(course: any, type: 'approve' | 'deny') {
    const selectedCourse = this._coursesData?.rows?.find((courseItem: any) => courseItem.id === course.itemId);
    if (type === 'deny') {
      const dialogRef = this.dialog.open(CatalogSuggestionsResponseComponent, { data: { course: selectedCourse, approvalType: type } });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.getSuggestions()
        }
      })
    } else {
      const editModal = this.dialog.open(UpdateCourseComponent, { data: { ...selectedCourse, isCatalogSuggestionsApproval: true } })
      editModal.afterClosed().subscribe((result) => {
        if (result) {
          this.getSuggestions()
        }
      })
    }
  }

  ngOnDestroy() {
    this.courseDataSubscription?.unsubscribe();
    this.entityTimezoneSubscription?.unsubscribe();
  }

}